import { ReactNode, memo, useEffect, useMemo, useState } from 'react';

import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { useEvent } from 'core/hooks';
import { useBreakpoints } from 'core/ui';
import { hasText } from 'core/utils';

import { AuthenticationScheme } from 'features/auth/constants';
import { useAuthentication } from 'features/auth/hooks/useAuthentication';

import {
  HIDDEN_APPLICATION_SHELL_PATHS,
  NAVBAR_DESKTOP_PREFERRED_WIDTH,
  NAVBAR_MINI_WIDTH,
  NAVBAR_MOBILE_PREFERRED_WIDTH,
  NAVIGATION_SIDEBAR_LOCAL_STORAGE_KEY,
} from '../constants';
import { NavigationSidebarContext } from '../contexts';
import { NavigationSidebarContextType } from '../types/NavigationSidebarContextType';
import { NavigationSidebarContent } from './NavigationSidebarContent';

type NavigationSidebar2Props = {
  children?: ReactNode;
};

type NavigationSidebarLocalStorage = {
  /** The expanded state of the navigation sidebar for desktop.  Mobile and desktop expanded state need to be persisted differently.  Initializing
   * a nav drawer that renders on top of the page (mobile) is at best annoying, so we don't do that.  However when the drawer is rendered
   * adjacent to the page (desktop), we want to restore the expanded state. */
  desktopExpanded: boolean;
  /** The expanded state of sections (navigation items that have children) within the navigation sidebar.  This is for both mobile and desktop. */
  expandedItemIds: string[];
};

export const NavigationSidebar = memo<NavigationSidebar2Props>(({ children }) => {
  const location = useLocation();
  const { mobile } = useBreakpoints();
  const { activeScheme } = useAuthentication();

  const [restoredState] = useState(() => {
    const rawValue = localStorage.getItem(NAVIGATION_SIDEBAR_LOCAL_STORAGE_KEY);
    const value = hasText(rawValue) ? (JSON.parse(rawValue) as NavigationSidebarLocalStorage) : null;
    return value;
  });
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [desktopExpanded, setDesktopExpanded] = useState(restoredState?.desktopExpanded ?? false);
  const [expandedItemIds, setExpandedItemIds] = useState<string[]>(restoredState?.expandedItemIds ?? []); // The ids of the items that are expanded.
  const [dialogEle, setDialogEle] = useState<HTMLDialogElement | null>(null);
  const [contentEle, setContentEle] = useState<HTMLDivElement | null>(null);

  const mode =
    activeScheme === AuthenticationScheme.SHARE || HIDDEN_APPLICATION_SHELL_PATHS.some((pathRegex) => location.pathname.match(pathRegex))
      ? 'hidden'
      : mobile
        ? 'dialog'
        : desktopExpanded
          ? 'full'
          : 'mini';

  const openMobileNavbar = useEvent(() => {
    if (mobile && !dialogEle?.hasAttribute('open')) {
      setMobileExpanded(true);
      dialogEle?.showModal();
    }
  });

  const handleToggleClick = useEvent(() => {
    if (!mobile) {
      const newExpanded = !desktopExpanded;
      const newLocalStorageState: NavigationSidebarLocalStorage = {
        desktopExpanded: newExpanded,
        expandedItemIds,
      };
      setDesktopExpanded(newExpanded);
      localStorage.setItem(NAVIGATION_SIDEBAR_LOCAL_STORAGE_KEY, JSON.stringify(newLocalStorageState));
    } else {
      const newExpanded = !mobileExpanded;
      setMobileExpanded(newExpanded);

      if (newExpanded) {
        dialogEle?.showModal();
      } else {
        dialogEle?.close();
      }
    }
  });

  const handleMobileDrawerDismiss = useEvent(() => {
    setMobileExpanded(false);
    dialogEle?.close();
  });

  const handleExpandItemChange = useEvent((itemId: string, expanded: boolean) => {
    const newLocalStorageState: NavigationSidebarLocalStorage = {
      desktopExpanded,
      expandedItemIds: expanded ? [...expandedItemIds, itemId] : expandedItemIds.filter((id) => id !== itemId),
    };
    localStorage.setItem(NAVIGATION_SIDEBAR_LOCAL_STORAGE_KEY, JSON.stringify(newLocalStorageState));
    setExpandedItemIds(newLocalStorageState.expandedItemIds);
  });

  const navbarContextValue: NavigationSidebarContextType = useMemo(
    () => ({
      mode,
      openMobileNavbar,
    }),
    [mode, openMobileNavbar],
  );

  // Handle transition from mobile to desktop.  If we are on desktop, but we are still showing the mobile drawer, we need to close it.
  useEffect(() => {
    if (!mobile && mobileExpanded) {
      setMobileExpanded(false);

      if (dialogEle?.hasAttribute('open')) {
        dialogEle?.close();
      }
    }
  }, [mobile, mobileExpanded, dialogEle]);

  // Dismiss the mobile drawer when the user clicks outside of the content.
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    if (!mobile || contentEle == null || !mobileExpanded) return () => {};

    const handleClick = (event: MouseEvent) => {
      if (!contentEle.contains(event.target as Node)) {
        setMobileExpanded(false);
        dialogEle?.close();
      }
    };

    // This useEffect is called typically within the same event loop as the "toggle" click event.  So we need to wait until the next
    // event loop so that we aren't treating the "toggle" click as a "dismiss" click.
    setTimeout(() => {
      document.addEventListener('click', handleClick);
    });

    return () => {
      setTimeout(() => {
        document.removeEventListener('click', handleClick);
      });
    };
  }, [mobile, contentEle, dialogEle, mobileExpanded]);

  return (
    <NavigationSidebarContext.Provider value={navbarContextValue}>
      <StyledComponentDiv id="navigation-sidebar">
        {(mode === 'full' || mode === 'mini') && (
          <NavigationSidebarContent
            ref={setContentEle}
            mode={mode}
            expandedItemIds={expandedItemIds}
            onExpandItemChange={handleExpandItemChange}
            onToggleClick={handleToggleClick}
          />
        )}
        {mode === 'dialog' && (
          <StyledDialog ref={setDialogEle}>
            <NavigationSidebarContent
              ref={setContentEle}
              mode={mode}
              expandedItemIds={expandedItemIds}
              onExpandItemChange={handleExpandItemChange}
              onDismiss={handleMobileDrawerDismiss}
            />
          </StyledDialog>
        )}

        <StyledPageWrapperDiv id="page-wrapper" $mode={mode}>
          {children}
        </StyledPageWrapperDiv>
      </StyledComponentDiv>
    </NavigationSidebarContext.Provider>
  );
});

NavigationSidebar.displayName = 'NavigationSidebar';

const StyledComponentDiv = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr); // Fill viewport and don't allow the content to expand horizontally.
  grid-template-rows: 1fr; // Default to fill viewport, but expand vertically if needed.
`;

const StyledDialog = styled.dialog`
  margin: 0;
  display: none;
  overflow: hidden;
  overscroll-behavior: contain;
  width: min(${NAVBAR_MOBILE_PREFERRED_WIDTH}px, 100dvw);
  height: 100dvh;
  padding: 0;
  border: none;
  max-width: unset;
  max-height: unset;
  translate: -100% 0;
  transition-property: overlay display translate;
  transition-duration: 250ms;
  transition-behavior: allow-discrete;
  transition-timing-function: cubic-bezier(0.25, 0.8, 0.25, 1);

  &[open] {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: 1fr;
    translate: 0 0;

    @starting-style {
      translate: -100% 0;
    }
  }

  &::backdrop {
    background-color: #000000;
    opacity: 0.3;
    transition-property: overlay display opacity;
    transition-duration: 250ms;
    transition-behavior: allow-discrete;
    transition-timing-function: cubic-bezier(0.25, 0.8, 0.25, 1);

    @starting-style {
      opacity: 0;
    }
  }

  &:not([open])::backdrop {
    opacity: 0;
  }
`;

const StyledPageWrapperDiv = styled.div<{ $mode: 'dialog' | 'mini' | 'full' | 'hidden' }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-auto-rows: 1fr;

  ${({ $mode }) =>
    $mode === 'dialog' || $mode === 'hidden'
      ? null
      : $mode === 'mini'
        ? `padding-left: ${NAVBAR_MINI_WIDTH}px;`
        : `padding-left: ${NAVBAR_DESKTOP_PREFERRED_WIDTH}px;`}
`;
